import { h, Fragment } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import register from "preact-custom-element";
import {
  saveZip,
  getZip,
  isValidZip,
  zipCountry,
} from "../../helpers/cacheZip";
import getGuideData from "../../helpers/getGuideData/getGuideData";
import getLocation from "../../helpers/getLocation/getLocation";
import getLatLong from "./getLatLong";
import haversine from "haversine";
import FindCertifiedGuideResult from "./find-certified-guide-result";

const PER_ROW = 2;

const filterGuides = ({ guideData, latLong }) =>
  guideData
    .map((guide) => ({
      ...guide,
      distance: haversine(guide, latLong, { unit: "mile" }),
    }))
    .sort((a, b) => a.distance - b.distance)
    .filter(
      (guide) => guide.distance <= window.theme.localCertifiedGuideRadius,
    );

function FindCertifiedGuide({ initialZip, hash, countryIsoCode }) {
  const [validating, setValidating] = useState(true);
  const [loading, setLoading] = useState(false);
  const [zip, setZip] = useState(getZip() || initialZip);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [showResultsCount, setShowResultsCount] = useState(PER_ROW);
  const zipText = countryIsoCode === "CA" ? "postal" : "zip";

  useEffect(() => {
    const validate = async () => {
      const [locationData, guideData] = await Promise.all([
        getLocation(),
        getGuideData(hash),
      ]);

      const localGuides = filterGuides({
        guideData,
        latLong: locationData.location,
      });

      if (localGuides.length) {
        setValidating(false);
      }
    };

    validate();
  }, []);

  const findCertifiedGuides = async () => {
    setLoading(true);
    const guideData = await getGuideData(hash);
    const latLong = await getLatLong(zip, zipCountry(zip));
    const localGuides = filterGuides({ guideData, latLong });

    setResults(localGuides);
    setLoading(false);
  };

  if (validating) return null;

  return (
    <div className="estimated-delivery">
      <div className="estimated-delivery__title">Find a Certified Guide</div>
      {loading && (
        <div className="estimated-delivery__loading">
          <div className="estimated-delivery__loading-line estimated-delivery__loading-line--line-1">
            <span className="visually-hidden">Loading</span>
          </div>
          <div className="estimated-delivery__loading-line estimated-delivery__loading-line--line-2"></div>
        </div>
      )}
      {!(results?.length > 0) && !loading && (
        <>
          <div className="find-certified-guide__form">
            <div className="estimated-delivery__input-wrapper">
              <label className="estimated-delivery__label">
                <span className="visually-hidden">{zipText} code</span>
                <input
                  form="none"
                  type="text"
                  className="estimated-delivery__input"
                  placeholder={`Enter ${countryIsoCode === "US" ? zipText.toUpperCase() : zipText} Code`}
                  required
                  onChange={(e) => {
                    saveZip(e.target.value);
                    setZip(e.target.value);

                    if (error && isValidZip(e.target.value)) {
                      setError(null);
                    }
                  }}
                  onFocusOut={() => {
                    const error = isValidZip(zip)
                      ? ""
                      : `Please enter a valid ${zipText} code.`;
                    setError(error);
                  }}
                  onKeyUp={(e) => {
                    if (e.key !== "Enter") return;

                    findCertifiedGuides();
                  }}
                  value={zip}
                />
              </label>
              <button
                type="button"
                className="estimated-delivery__submit"
                onClick={() => findCertifiedGuides()}
                disabled={!isValidZip(zip)}
              >
                Submit
              </button>
            </div>
            {error && <div className="estimated-delivery__error">{error}</div>}
          </div>
        </>
      )}
      {!error && !loading && results?.length === 0 && (
        <div className="find-certified-guide__no-results">
          Apologies, but there are no Certified Guides near{" "}
          <strong>{zip}</strong>
          <br />
          Please try a different {zipText} code.
        </div>
      )}
      {!error && !loading && results?.length > 0 && (
        <div className="find-certified-guide__results">
          <div className="find-certified-guide__results-header">
            <div className="find-certified-guide__results-title">
              <span>Certified Guides near </span>
              <strong>{zip}</strong>
            </div>
            <button
              type="button"
              className="find-certified-guide__results-edit"
              onClick={() => {
                setResults(null);
                setShowResultsCount(PER_ROW);
              }}
            >
              Edit
            </button>
          </div>
          <ul className="find-certified-guide__results-list">
            {results
              .slice(0, Math.min(results.length, showResultsCount))
              .map((result) => (
                <FindCertifiedGuideResult
                  result={result}
                  key={result.name}
                  zip={zip}
                  countryIsoCode={countryIsoCode}
                />
              ))}
          </ul>
          {showResultsCount < results.length && (
            <button
              type="button"
              className="find-certified-guide__show-more"
              onClick={() => setShowResultsCount(showResultsCount + PER_ROW)}
            >
              Show more
            </button>
          )}
        </div>
      )}
    </div>
  );
}

if (!customElements.get("find-certified-guide")) {
  register(
    FindCertifiedGuide,
    "find-certified-guide",
    ["initial-variant-id", "updated-at", "country-iso-code"],
    {
      shadow: false,
    },
  );
}
